import { useTranslation } from 'react-i18next';

import { DonutGauge } from './charts';
import { SectionHeadCard } from '../../../../components';
import { noteLabel } from '../../../../core';
import { registerHistoryPct } from '../../resultCopy';

import type { DiagnosisRecord } from '../../record';

/**
 * 음정 (시안 §8.5 `994:7243`) — `음정 정확도` 도넛 + 음역 다섯 구간의 안정성(레드 막대)과
 * **직전 회차 대비**(오렌지 막대).
 *
 * 구간이 하나도 없으면 도넛 아래 **한 줄로 `이번엔 못 쟀어요`**라고만 말한다: 음역을 못 잰
 * 회차(저음·고음을 건너뛰었다)는 나눌 것이 없어 다섯 줄을 세울 수 없고, 빈 다섯 줄은
 * "다 못 냈다"로 읽힌다.
 */

/** 안정성 막대 130px · 이력 막대 80px — 시안 `994:7627`/`994:7631`. */
const BAR = { stability: 130, history: 80 };
/** 이력 막대는 100%를 가득으로 본다. 넘치면(105%) 가득 찬 채 숫자가 말한다. */
const clampPct = (value: number): number => Math.min(Math.max(value, 0), 100);

const Bar = ({
  width,
  ratio,
  tone,
  label,
}: {
  width: number;
  ratio: number | null;
  tone: 'primary' | 'orange';
  label: string;
}) => (
  // 막대 폭은 시안 값이 상한이다 — 좁은 화면(320px)에서는 두 막대가 같이 줄어든다. 고정
  // 폭으로 두면 줄이 화면 밖으로 밀려 이력 대비 막대가 잘렸다.
  <span className="flex min-w-0 items-center gap-2">
    <span
      className="h-1.5 min-w-0 shrink overflow-hidden rounded-[3px] bg-bk-200"
      style={{ width }}
      aria-hidden="true"
    >
      {ratio !== null && (
        <span
          className={
            tone === 'primary'
              ? 'block h-full rounded-[3px] bg-primary'
              : 'block h-full rounded-[3px] bg-orange'
          }
          style={{ width: `${clampPct(ratio)}%` }}
        />
      )}
    </span>
    <span className="shrink-0 whitespace-nowrap text-[12.5px] font-semibold tabular-nums text-bk-800">
      {label}
    </span>
  </span>
);

export const PitchRegisterSection = ({
  record,
  previous,
}: {
  record: DiagnosisRecord;
  /** 이력 대비의 기준선. 첫 회차에는 null이고, 그때 비교 칸은 `—`다(0%가 아니다). */
  previous: DiagnosisRecord | null;
}) => {
  const { t } = useTranslation();
  const registers = record.pitchRegisters;
  /**
   * 머리의 `음정 정확도` 도넛. **없으면 null이다.** 회차에 담기는 축은 `STORED_METRIC_KEYS`가
   * 이미 걸러 놓은 것들이라, 배열에 없다는 것이 곧 "못 쟀다"다. 0으로 떨어뜨리면 링이
   * 비어 있는 채 "0점"을 말한다.
   */
  const accuracyScore =
    record.scores.find((entry) => entry.key === 'pitchAccuracy')?.val ?? null;

  return (
    <div className="flex flex-col gap-10 py-4">
      <div className="flex flex-col gap-8">
        <SectionHeadCard
          size="md"
          className="mx-[18px]"
          title={t('web.result.registers.accuracy')}
        />
        <DonutGauge
          score={accuracyScore}
          label={t('web.result.registers.accuracy')}
        />
      </div>

      {!registers || registers.length === 0 ? (
        <p className="text-center text-sm font-semibold text-bk-600">
          {t('web.result.pending')}
        </p>
      ) : (
        <div className="flex flex-col gap-10">
          <div className="flex flex-col items-center gap-3">
            <p className="text-base font-medium tracking-[-0.08px] text-bk-800">
              {t('web.result.registers.intro')}
            </p>
            <div className="flex items-center gap-3.5 text-[13px] font-medium text-bk-800">
              <span className="flex items-center gap-2">
                <span
                  aria-hidden="true"
                  className="h-2 w-2 rounded-full bg-primary"
                />
                {t('web.result.registers.legendStability')}
              </span>
              <span className="flex items-center gap-2">
                <span
                  aria-hidden="true"
                  className="h-2 w-2 rounded-full bg-orange"
                />
                {t('web.result.registers.legendHistory')}
              </span>
            </div>
          </div>

          <ol className="flex flex-col gap-[17px] px-[18px]">
            {registers.map(({ id, lowMidi, highMidi, score }, i) => {
              // 이력 대비 칸. 비교할 것이 없으면 **`—`**다 — 0%로 두면 첫 회차가 "다
              // 잃었다"로 읽힌다.
              const pct =
                score == null ? null : registerHistoryPct(score, previous, id);
              return (
                <li key={id} className="flex items-center gap-3.5">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-sm font-bold text-bk-900 shadow-chip">
                    {i + 1}
                  </span>
                  <div className="flex min-w-0 flex-1 flex-col gap-3.5">
                    <div className="flex flex-col gap-1.5">
                      <p className="text-[15px] font-semibold tracking-[-0.075px] text-bk-900">
                        {t(`web.result.registers.name.${id}`)}
                      </p>
                      {/* 경계는 레코드가 들고 있다 — 화면이 다시 계산하면 옛 회차를 오늘의
                          음역으로 그린다. */}
                      <p className="text-[13px] font-medium text-bk-800">
                        {t('web.result.registers.spanShort', {
                          low: noteLabel(t, lowMidi),
                          high: noteLabel(t, highMidi),
                          desc: t(`web.result.registers.desc.${id}`),
                        })}
                      </p>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <Bar
                        width={BAR.stability}
                        ratio={score ?? null}
                        tone="primary"
                        label={
                          score == null
                            ? t('web.result.pending')
                            : String(Math.round(score))
                        }
                      />
                      <Bar
                        width={BAR.history}
                        ratio={pct}
                        tone="orange"
                        label={
                          pct == null
                            ? t('web.result.registers.historyNone')
                            : t('web.result.registers.history', { value: pct })
                        }
                      />
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </div>
  );
};
